import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Tabs, Tab, Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { apiClient, apiClientRaw } from '../utils/api';
import { toast } from 'react-toastify';
 
import { clearAuthData , updateTocAccepted } from '../../store/slices/authSlice';
import { resetLawfirm } from '../../store/slices/lawFirmSlice';
import { persistor } from '../../store/store';

const Dashboard = ({ title, links }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userdetails = useSelector((state) => state.auth.user);
  const refreshToken = localStorage.getItem('refreshToken');

  const [openTerms, setOpenTerms] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (userdetails && userdetails.toc_accepted === false) {
      setOpenTerms(true);
    }
  }, [userdetails]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleAccept = async () => {
    try {
      await apiClient('/toc-accept', 'PATCH');
      toast.success('Terms accepted successfully.');
      dispatch(updateTocAccepted());  // ✅ update Redux state
      setOpenTerms(false);
    } catch (error) {
      toast.error('Failed to accept terms. Please try again.');
    }
  };
  
  const handleReject = async () => {
    setLoggingOut(true);
    // Remove all sensitive data and tokens
    dispatch(clearAuthData());
    dispatch(resetLawfirm());
    localStorage.removeItem('accessToken');
    localStorage.removeItem('lastVisitedPath');
    localStorage.removeItem('count');
    if (persistor && persistor.purge) {
      persistor.purge();
    }
    navigate('/');
    toast.info('You must accept the Terms to continue. You have been logged out.');

    try {
      const response = await apiClientRaw('/logout/', 'POST', { refresh: refreshToken });

      if (response.status === 205) {
        // Already cleared above
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoggingOut(false);
    }
  };

  const ButtonStyles = {
    textTransform: 'none',
    width: '100%',
    height: '152px',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)',
    color: '#333333',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    border: '1px solid rgba(37, 99, 235, 0.2)',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  };

  return (
    <Box p={3} gap={3} display="flex" flexDirection="column" alignItems="center">
      <Typography variant="h6" fontFamily="sans-serif" fontWeight="800" mb={2} sx={{ color: 'black' }}>
        {title}
      </Typography>

      {links.map((linkGroup, index) => (
      <Box key={index} display="flex" gap={3} width="100%">
        {linkGroup.map((link, idx) => (
          <Button
            key={idx}
            onClick={() => navigate(link.path)}
            style={{
              ...ButtonStyles,
              transition: 'all 0.2s ease-in-out',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.15)';
              e.currentTarget.style.background = 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 50%, #e9ecef 100%)';
              e.currentTarget.style.borderColor = 'rgba(37, 99, 235, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)';
              e.currentTarget.style.borderColor = 'rgba(37, 99, 235, 0.2)';
            }}
            onMouseDown={e => {
              e.currentTarget.style.transform = 'scale(0.98)';
            }}
            onMouseUp={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
          >
            {React.cloneElement(link.icon, {
              style: { fontSize: 40, color: '#2563eb' },
            })}
            <Typography variant="h6" mt={2} sx={{ color: '#333333' }}>
              {link.name}
            </Typography>
          </Button>
        ))}
      </Box>
    ))}


      <Dialog
        open={openTerms}
        onClose={() => {}}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: '10px',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
          Website Terms of Use
        </DialogTitle>
        <Divider />
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Website Terms of Use" />
          <Tab label="Privacy Policy for California Residents" />
        </Tabs>
        <DialogContent
          dividers
          sx={{ height: '70vh', p: 2, backgroundColor: '#fafafa' }}
        >
          {selectedTab === 0 && (
            <iframe
              src="/toc.pdf"
              title="Terms of Use PDF"
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }}
            />
          )}
          {selectedTab === 1 && (
            <iframe
              src="/ccpa.pdf"
              title="Privacy Policy PDF"
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Documents provided by Vermost & Associates, LLC
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              onClick={handleReject}
              variant="outlined"
              color="error"
              disabled={loggingOut}
            >
              Reject
            </Button>
            <Button
              onClick={handleAccept}
              variant="contained"
              color="primary"
            >
              Accept
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
